import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';

import { getCertificateTermsTemplateByIdAsync } from '@share/api';
import type { ICertificateTermsTemplate } from '@share/common-types';

export interface ICertificateTermsError {
  title: string;
  detail: string;
  status: number | null;
}

export interface ICertificateTermsState {
  certificateTerms: ICertificateTermsTemplate | null;
  loading: boolean;
  error: ICertificateTermsError | null;
}

const initialState: ICertificateTermsState = {
  certificateTerms: null,
  loading: false,
  error: null,
};

function parseCertificateTermsError(error: unknown): ICertificateTermsError {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;

    if (data && typeof data === 'object') {
      const titleValue = 'title' in data ? data.title : undefined;
      const detailValue = 'detail' in data ? data.detail : undefined;
      const statusValue = 'status' in data ? data.status : undefined;

      if (typeof titleValue === 'string') {
        return {
          title: titleValue,
          detail: typeof detailValue === 'string' ? detailValue : '',
          status: typeof statusValue === 'number' ? statusValue : null,
        };
      }
    }
  }

  if (error instanceof Error) {
    return {
      title: error.message,
      detail: '',
      status: null,
    };
  }

  return {
    title: 'Unknown error',
    detail: '',
    status: null,
  };
}

export const fetchCertificateTermsById = createAsyncThunk<
  ICertificateTermsTemplate,
  string,
  { rejectValue: ICertificateTermsError }
>('certificateTerms/fetchById', async (id, { rejectWithValue }) => {
  try {
    const { data } = await getCertificateTermsTemplateByIdAsync(id);
    return data;
  } catch (error: unknown) {
    return rejectWithValue(parseCertificateTermsError(error));
  }
});

const certificateTermsSlice = createSlice({
  name: 'certificateTerms',
  initialState,
  reducers: {
    clearCertificateTerms: (state: ICertificateTermsState) => {
      state.certificateTerms = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCertificateTermsById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCertificateTermsById.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.certificateTerms = payload;
      })
      .addCase(fetchCertificateTermsById.rejected, (state, { payload }) => {
        state.loading = false;
        state.certificateTerms = null;
        state.error = payload ?? {
          title: 'Unknown error',
          detail: '',
          status: null,
        };
      });
  },
});

export const certificateTermsActions = certificateTermsSlice.actions;

export const certificateTermsReducer = certificateTermsSlice.reducer;
